/**
 * shared/components/ErrorBoundary.js
 * Class-based error boundary shared across all frontends.
 * Identical to src/components/ErrorBoundary.js — import path adjusted.
 */
import React from "react";
import theme from "../theme";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("App Crash:", error);
    console.error("Component Stack:", info.componentStack);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      fetch(`${BACKEND_URL}/log/client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          error:          error?.toString() ?? "Unknown error",
          componentStack: info?.componentStack ?? "",
          url:            window.location.href,
          userAgent:      navigator.userAgent,
        }),
      }).catch(() => {});
    } catch (_) {}
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === "development";

      return (
        <div
          style={{
            height: "100dvh",
            background: theme.colors.bg,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            fontFamily: theme.font.family,
            padding: "24px",
            textAlign: "center",
          }}
        >
          <div style={{
            width: 56,
            height: 56,
            borderRadius: theme.radius.md,
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#991B1B",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            fontWeight: 900,
            marginBottom: 16,
          }}>
            !
          </div>

          <h2 style={{ color: theme.colors.textDark, fontWeight: 900, marginBottom: 8, fontSize: 22 }}>
            Something went wrong
          </h2>

          <p style={{ color: theme.colors.textLight, fontSize: 14, marginBottom: 24, maxWidth: 340, lineHeight: 1.55 }}>
            The app hit an unexpected problem. Try again, or reload if the screen does not recover.
          </p>

          {isDev && this.state.error && (
            <pre
              style={{
              background: "#0F172A",
              color: "#FCA5A5",
              padding: "12px 16px",
              borderRadius: theme.radius.md,
              fontSize: 11,
              textAlign: "left",
              maxWidth: 420,
              overflowX: "auto",
              marginBottom: 24,
              border: "1px solid rgba(255,255,255,0.10)",
            }}
            >
              {this.state.error.toString()}
            </pre>
          )}

          <button
            onClick={this.handleReset}
            style={{
              background: theme.colors.primary,
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: theme.radius.sm,
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              marginBottom: 10,
              width: 200,
              boxShadow: theme.shadow.sm,
            }}
          >
            Try again
          </button>

          <button
            onClick={this.handleReload}
            style={{
              background: "white",
              color: theme.colors.textDark,
              border: `1px solid ${theme.colors.borderStrong}`,
              padding: "12px 24px",
              borderRadius: theme.radius.sm,
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              width: 200,
            }}
          >
            Reload app
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
